import { NULL_ERROR } from '@whistler-ui/k2/constants';
import { removeQuotesFromText } from '@whistler-ui/k2/helpers';
import { SilentParam } from '@whistler-ui/k2/types/common';

import { decodeBase64, encodeBase64 } from '../helpers/encoders';
import {
  Environment,
  MtlsVerification,
  MtlsVerificationCreated,
} from '../types';
import { requestWithOptions } from './helper';

const basePath = import.meta.env.REACT_APP_API_ENDPOINT_ENVIRONMENT || '/';

export const getInboundCSR = async ({
  environmentId,
  silent = true,
}: SilentParam<{ environmentId: string }>) => {
  try {
    const options = {
      method: 'GET',
      'Content-Type': 'text/plain',
    };

    const path = `${basePath}/environments/${environmentId}/cert`;

    const response = await requestWithOptions(path, options);

    return { ...response, inbound_cert: decodeBase64(response.inbound_cert) };
  } catch (error) {
    const errorMsg = removeQuotesFromText((error as Error | TypeError).message);
    console.error(errorMsg);

    if (silent) {
      return null;
    }

    throw error;
  }
};

export const postCSR = async ({
  payload,
  silent = true,
}: SilentParam<{ payload: any }>) => {
  try {
    const options = {
      method: 'POST',
      body: JSON.stringify({
        csr: encodeBase64(payload.csr),
      }),
    };

    const path = `${basePath}/environments/${payload.environmentId}/csr`;

    const response = await requestWithOptions(path, options);

    return { ...response, inbound_cert: decodeBase64(response.inbound_cert) };
  } catch (error) {
    const errorMsg = removeQuotesFromText((error as Error | TypeError).message);
    console.error(errorMsg);

    if (silent) {
      return null;
    }

    throw error;
  }
};

export const putEnvironments = async (
  payload: any,
  {
    withCert = true,
  }: {
    withCert?: boolean;
  } = {},
) => {
  const body = withCert
    ? { ...payload, cert: encodeBase64(payload.cert) }
    : payload;

  const options = {
    method: 'PUT',
    body: JSON.stringify(body),
  };

  const path = `${basePath}/environments`;
  const response = await requestWithOptions(path, options);

  return { ...response, cert: decodeBase64(response.cert) };
};

export const getEnvironments = async () => {
  try {
    const options = { method: 'GET' };
    const path = `${basePath}/environments`;

    const response: Environment[] = await requestWithOptions(path, options);

    const result = response.map(env => ({
      ...env,
      cert: decodeBase64(env.cert),
    }));

    return result;
  } catch (e) {
    if (e.message === NULL_ERROR) {
      return [];
    }

    throw e;
  }
};

export const getCSR = async environmentId => {
  try {
    const options = {
      method: 'GET',
      'Content-Type': 'text/plain',
    };
    const path = `${basePath}/environments/${environmentId}/csr`;
    const response = await requestWithOptions(path, options);

    return response;
  } catch (e) {
    return null;
  }
};

export const isValidFeature = async (
  featureName: string,
  environmentId: string,
) => {
  try {
    const path = `${basePath}/environments/feature/${featureName}?environment_id=${environmentId}`;
    const options = { method: 'GET' };
    const response = await requestWithOptions(path, options);
    return response as boolean;
  } catch (e) {
    return false;
  }
};

export const postMtlsVerification = async (environmentId: string) => {
  const options = {
    method: 'POST',
    body: JSON.stringify({}),
  };

  const path = `${basePath}/environments/${environmentId}/mtls-verification`;
  const response: MtlsVerificationCreated = await requestWithOptions(
    path,
    options,
  );

  return response;
};

export const getLatestMtlsVerification = async (environmentId: string) => {
  try {
    const options = { method: 'GET' };
    const path = `${basePath}/environments/${environmentId}/mtls-verification/latest`;
    const response: MtlsVerification = await requestWithOptions(path, options);

    return response;
  } catch (e) {
    // No verification recorded yet (or a transient poll failure). Callers
    // poll until a matching run settles, so absence is null, not an error.
    return null;
  }
};
